/**
 * Le paramétrage du calendrier ouvré — réservé à l'administrateur.
 *
 * *Demande de Chris, 15/08/2026 : « l'admin pourra paramétrer des jours non travaillés en
 * plus des jours fériés, donc dans un menu paramétrage du calendrier. »*
 *
 * Les onze fériés français ne sont **pas** dans ce tableau, et c'est le point à
 * comprendre : ils sont calculés, Pâques comprise, donc justes pour toutes les années à
 * venir. Les lister ici obligerait à ressaisir onze dates chaque année, et un oubli
 * ferait planifier un 1er mai sans qu'aucune erreur ne le signale.
 *
 * L'écran affiche donc ce que le calendrier français ne sait pas : les ponts, les
 * fermetures, et les samedis travaillés.
 */
import { useEffect, useState } from "react";
import type { JSX } from "react";
import { empechement, estDateCivile, formaterDateLongue } from "@planif/moteur";
import { chargerCalendrier, poserException, retirerException } from "./api";
import type { JourException } from "./api";

/** Ce que le calendrier français dit déjà de ce jour, pour que l'admin ne se répète pas. */
function ceQuIlEstDeja(jour: string): string {
  if (!estDateCivile(jour)) return "";
  const cause = empechement(jour);
  if (cause === null) return "jour ouvré";
  return cause === "week-end" ? "week-end" : cause.ferie;
}

export function Calendrier(): JSX.Element {
  const [exceptions, setExceptions] = useState<readonly JourException[] | null>(null);
  const [jour, setJour] = useState("");
  const [chome, setChome] = useState(true);
  const [motif, setMotif] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  useEffect(() => {
    chargerCalendrier()
      .then((r) => {
        setExceptions(r.exceptions);
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      });
  }, []);

  const agir = (promesse: Promise<{ exceptions: readonly JourException[] }>): void => {
    setOccupe(true);
    setErreur(null);
    promesse
      .then((r) => {
        setExceptions(r.exceptions);
        setJour("");
        setMotif("");
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setOccupe(false);
      });
  };

  const deja = ceQuIlEstDeja(jour);
  // Poser « chômé » sur un samedi ne change rien, et poser « travaillé » sur un jour déjà
  // ouvré non plus : l'écran le dit AVANT l'enregistrement plutôt que d'accepter une
  // ligne sans effet que personne ne comprendra dans six mois.
  const sansEffet =
    deja !== "" && ((chome && deja !== "jour ouvré") || (!chome && deja === "jour ouvré"));

  return (
    <section className="referentiel">
      <h3>Calendrier ouvré</h3>
      <p className="etat">
        Les samedis, les dimanches et les onze jours fériés français sont déjà connus de
        l'application — ils se calculent, ils ne se saisissent pas. Ce tableau ne porte que
        les écarts : les jours que l'entreprise chôme en plus, et ceux qu'elle travaille
        exceptionnellement. Un jour posé ici décale les plannings de tous les chantiers.
      </p>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      <form
        className="selection"
        onSubmit={(e) => {
          e.preventDefault();
          if (!estDateCivile(jour)) return;
          agir(poserException({ jour, chome, motif: motif.trim() === "" ? null : motif }));
        }}
      >
        <label>
          Jour
          <input
            type="date"
            value={jour}
            onChange={(e) => {
              setJour(e.target.value);
            }}
            required
          />
        </label>
        <label>
          Nature
          <select
            value={chome ? "chome" : "travaille"}
            onChange={(e) => {
              setChome(e.target.value === "chome");
            }}
          >
            <option value="chome">Chômé — l'entreprise ne travaille pas</option>
            <option value="travaille">Travaillé exceptionnellement</option>
          </select>
        </label>
        <label>
          Motif
          <input
            type="text"
            value={motif}
            placeholder="pont de l'Ascension, fermeture d'août…"
            onChange={(e) => {
              setMotif(e.target.value);
            }}
          />
        </label>
        <button type="submit" disabled={occupe || !estDateCivile(jour)}>
          Poser
        </button>
      </form>

      {deja !== "" && (
        <p className={sansEffet ? "alerte" : "etat"}>
          Ce jour est aujourd'hui : <strong>{deja}</strong>.
          {sansEffet && " Poser cette exception ne changerait donc rien."}
        </p>
      )}

      {exceptions === null ? (
        <p className="etat">Chargement…</p>
      ) : exceptions.length === 0 ? (
        <p className="etat">
          Aucune exception : le calendrier est celui de la République, week-ends compris.
        </p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Jour</th>
              <th>Nature</th>
              <th>Motif</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {exceptions.map((exception) => (
              <tr key={exception.jour}>
                <td>{formaterDateLongue(exception.jour)}</td>
                <td>
                  {exception.chome ? (
                    "Chômé"
                  ) : (
                    <span className="en-ecart">Travaillé exceptionnellement</span>
                  )}
                </td>
                <td>{exception.motif ?? <span className="note">—</span>}</td>
                <td>
                  <button
                    type="button"
                    className="bouton-danger"
                    disabled={occupe}
                    onClick={() => {
                      agir(retirerException(exception.jour));
                    }}
                  >
                    Retirer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
